<template>
  <div>
    <ModalConfirmationCheckable
      id="printConfirmationModal"
      ref="printConfirmationModal"
      :name="nameModal"
      :store-name="storeNameModal"
      :checkable="modalConfirmationCheckMessage"
      :content="modalConfirmationMessage"
      :ui-component="uiComponentModal"
    />
    <ModalPrintMagazine ref="modalMagazineSelector" />
    <modal-wrapper ref="modalPrint">
      <modal-print />
    </modal-wrapper>
    <modal-wrapper ref="modalPrintConfiguration">
      <modal-print-configuration
        @showModalUserConf="showUserConfModal"
        @showModalUserConfTracking="showUserConfTrackingModal"
      />
    </modal-wrapper>
    <modal-wrapper ref="userConfModal">
      <modal-user-conf :tab-position="2" />
    </modal-wrapper>
    <modal-wrapper ref="userConfTrackingModal">
      <modal-user-conf-tracking />
    </modal-wrapper>
  </div>
</template>

<script>
import anapathPrinterRestClient from '@/modules/printModule/services/AnapathPrinterRestClient'
import ModalPrintMagazine from '@/modules/sampleProcessingModule/views/form/modals/ModalPrintMagazine'
import clinicalRequestPrintService from '@/modules/printModule/services/ClinicalRequestPrintService'
import clinicalSubrequestPrintService from '@/modules/printModule/services/ClinicalSubequestPrintService'
import registrySubrequestPrintService from '@/modules/printModule/services/RegistrySubrequestPrintService'
import { registryRequestPrintService } from '@/modules/printModule/services/RegistryRequestPrintService'
import clinicalCasePrintService from '@/modules/printModule/services/ClinicalCasePrintService'
import { registryCasePrintService } from '@/modules/printModule/services/registry-case-print-service'
import samplePrintService from '@/modules/printModule/services/SamplePrintService'
import ModalConfirmationCheckable from '@/components/modals/ModalConfirmationCheckable'
import portionPrintService from '@/modules/printModule/services/PortionPrintService'
import subportionPrintService from '@/modules/printModule/services/SubportionPrintServices'
import { groupBy } from 'lodash'
import ModalPrint from '@/components/modals/ModalPrint'
import ModalPrintConfiguration from '@/components/modals/ModalPrintConfiguration'
import ModalUserConf from '@/components/userConfiguration/ModalUserConf'
import ModalUserConfTracking from '@/components/userConfiguration/ModalUserConfTracking'
import { CASSETTE_LABEL_REPORT_MODELS, PRINTMATE_CASSETTE_LABEL_REPORT_MODEL, reportType } from '@/modules/printModule/print-component.model'
import uuid from 'uuid'
import customConfigResource from '@/api/resources/ui/CustomConfigResource'
import { store } from '@/store/store'
import { ModalUtils } from '@/shared/util/modal-utils'
import { PromiseUtils } from '@/shared/util/promise-utils'
import { StoreUtils } from '@/shared/util/store-utils'
import { ToastUtils } from '@/shared/util/toast-utils'

export default {
    components: {
        ModalConfirmationCheckable,
        ModalPrintMagazine,
        ModalPrint,
        ModalPrintConfiguration,
        ModalUserConf,
        ModalUserConfTracking
    },

    data() {
        return {
            nameModal: '',
            modalConfirmationMessage: '',
            modalConfirmationCheckMessage: '',
            uiComponentModal: 'uicomponent.print.modalconfig',
            storeNameModal: 'printModalsState'
        }
    },
    methods: {
        getModalConfirmationState(nameModal) {
            return StoreUtils.getModalConfirmationState(this.storeNameModal, nameModal)
        },
        async loadModalConfiguration() {
            const modalState = store.state.modalsState[this.storeNameModal]
            if (!modalState) {
                const customConfig = await customConfigResource.get(this.uiComponentModal)
                if (customConfig) {
                    await StoreUtils.setModalConfirmationState(this.storeNameModal, customConfig.config)
                }
            }
        },

        /**
         * Given a function that returns a promise of blob,
         * calls the associated configured printer to print the report.
         *
         * IMPORTANT: DO NOT CREATE MORE METHODS -> ¡¡Use this!!
         *
         * @param reportType
         * @param reportPromiseFunc: () => Promise<Blob>
         * @returns {Promise<void>}
         */
        async printReport(reportType, reportPromiseFunc) {
            StoreUtils.updateLoading(true)
            if (await this.getPrintServiceStatus()) {
                const device = await this.getConfiguredDevice(reportType)
                if (device) {
                    const report = await reportPromiseFunc()
                    await this.sendReportToReportsDevice(report, device)
                } else {
                    ModalUtils.showErrorModal(this.$tc('printService.notConfigured'))
                }
            }
            StoreUtils.updateLoading(false)
        },

        async printCaseLabel(labId, caseStudy, numberOfCopies) {
            await this.printReport(reportType.CASE_LABEL, async () => {
                if (await this.checkIfRepeatPrintingInSingleEntity(caseStudy, reportType.CASE_LABEL)) {
                    return PromiseUtils.toCallSecure(
                        registryCasePrintService.getCaseLabels(labId, caseStudy.casId, numberOfCopies ?? 1))
                }
                return null
            })
        },

        async printSampleLabelsFromRegistry(laboratoryId, casId, samples, withUserDefaultNumberOfCopies) {
            let workstation = ''
            if (withUserDefaultNumberOfCopies) {
                const [err, response] = await this.toCall(anapathPrinterRestClient.getWorkstation())
                if (err) {
                    this.showErrorModalGlobal(this.$t('printService.titleoff'))
                } else {
                    workstation = response
                }
            }
            StoreUtils.updateLoading(true)
            const printServiceStatus = await this.getPrintServiceStatus()
            StoreUtils.updateLoading(false)
            if (printServiceStatus) {
                let device = await this.getConfiguredDevice(reportType.SAMPLE_LABEL)
                if (!device) {
                    this.showErrorModalGlobal(this.$t('printService.notConfigured'))
                    return false
                }
                let samplesToPrint = await this.checkIfRepeatedPrintingInEntitiesList(samples, reportType.SAMPLE_LABEL)
                const defaultNumberOfCopies = await this.getDefaultNumberOfCopiesByReportCode(reportType.SAMPLE_LABEL.report)
                if (samplesToPrint.length !== 0) {
                    StoreUtils.updateLoading(true)
                    const samplesBySampleType = groupBy(samplesToPrint, 'sampleTypeId')
                    const sampleTypeKeys = Object.keys(samplesBySampleType)
                    for (const eachKey of sampleTypeKeys) {
                        let samplesWithCopies = {}
                        samplesBySampleType[eachKey].forEach((item) => {
                            samplesWithCopies[item.id] = item.copies ? item.copies : defaultNumberOfCopies
                        })

                        if (samplesToPrint.length !== 0) {
                            const response = await samplePrintService.getLabelReportPdfWithCopiesNumberForPrinting(laboratoryId, casId, samplesWithCopies, withUserDefaultNumberOfCopies, workstation)
                            const report = response.data
                            const reportModel = response.headers.reportmodel
                            if (reportModel) {
                                const [err, configuredDevice] = await this.toCall(this.getConfiguredDevice({ report: reportModel }))
                                if (err) {
                                    this.showErrorModalGlobal(this.$t('printService.notConfigured'))
                                    StoreUtils.updateLoading(false)
                                    return false
                                } else {
                                    if (configuredDevice) {
                                        device = configuredDevice
                                    }
                                }
                            }
                            return this.sendLabelsToLabelsDevice(report, device)
                        }
                    }
                }
            }
        },

        async printSubportionsLabels(laboratoryId, casId, samId, porId, subportions) {
            StoreUtils.updateLoading(true)
            const printServiceStatus = await this.getPrintServiceStatus()
            StoreUtils.updateLoading(false)
            if (printServiceStatus) {
                const device = await this.getConfiguredDevice(reportType.SUBPORTION_LABEL)
                if (!device) {
                    ModalUtils.showErrorModal(this.$tc('printService.notConfigured'))

                    return new Promise((resolve) => {
                        this.$eventHub.$on('onModalStatesErrorHide', () => {
                            this.$eventHub.$off('onModalStatesErrorHide')
                            resolve(false)
                        })
                    })
                }
                const subportionsToPrint = await this.checkIfRepeatedPrintingInEntitiesList(subportions, reportType.SUBPORTION_LABEL)
                if (subportionsToPrint.length !== 0) {
                    StoreUtils.updateLoading(true)
                    // TODO: Prefix deletion refactoring
                    const [err, report] = await this.toCall(subportionPrintService.getSubportionLabelsReportForPrinting(laboratoryId, casId, samId, porId, subportionsToPrint.map((item) => item.supId || item.subportionId)))
                    if (err) {
                        this.showErrorModalGlobal()
                        StoreUtils.updateLoading(false)
                        return false
                    }
                    const sendToDeviceResponse = await this.sendLabelsToLabelsDevice(report, device)
                    StoreUtils.updateLoading(false)
                    return sendToDeviceResponse
                }
            }
        },

        async printRequestReportFromClinical(request) {
            await this.printRequestReport(request, 'clinical')
        },

        async printRequestReportFromRegistry(request) {
            await this.printRequestReport(request, 'registry')
        },

        async printRequestReport(request, module) {
            const printServiceStatus = await this.getPrintServiceStatus()
            if (printServiceStatus) {
                const device = await this.getConfiguredDevice(reportType.REQUEST_REPORT)
                if (!device) {
                    this.showErrorModalGlobal(this.$t('printService.notConfigured'))
                    return
                }
                if (await this.checkIfRepeatPrintingInSingleEntity(request, reportType.REQUEST_REPORT)) {
                    let numberOfCopies = await this.getDefaultNumberOfCopiesByReportCode(reportType.REQUEST_REPORT.report)
                    let report = 'null'
                    switch (module) {
                    case 'clinical':
                        report = await clinicalRequestPrintService.getRequestReportPdfForPrinting([request.id])
                        break
                    case 'registry':
                        const labId = request.laboratoryLabId ?? request.laboratory.labId
                        report = await registryRequestPrintService.getRequestReport(labId, [request.id], true)
                        break
                    }
                    for (let copy = 0; copy < numberOfCopies; copy++) {
                        await this.sendReportToReportsDevice(report, device)
                    }
                }
            }
        },

        async printSubrequestReportFromClinical(laboratoryId, requestId, subrequest) {
            await this.printSubrequestLabels(laboratoryId, requestId, subrequest, 'clinical')
        },

        async printSubrequestReportFromRegistry(laboratoryId, requestId, subrequest) {
            await this.printSubrequestLabels(laboratoryId, requestId, subrequest, 'registry')
        },

        async printSubrequestLabels(laboratoryId, requestId, subrequests, module) {
            StoreUtils.updateLoading(true)
            const printServiceStatus = await this.getPrintServiceStatus()
            StoreUtils.updateLoading(false)
            if (printServiceStatus) {
                const device = await this.getConfiguredDevice(reportType.SUBREQUEST_LABEL)
                if (!device) {
                    this.showErrorModalGlobal(this.$t('printService.notConfigured'))
                    return
                }
                let subrequestsToPrint = []
                const defaultNumberOfCopies = await this.getDefaultNumberOfCopiesByReportCode(reportType.SUBREQUEST_LABEL.report)

                for (let subrequest of subrequests) {
                    if (subrequest.surPrinted) {
                        await new Promise((resolve) => {
                            this.modalConfirmationMessage = subrequest.surExternalId + ': ' + this.$tc('print.modals.subrequestPrinted')
                            this.modalConfirmationCheckMessage = this.$tc('print.modals.printedCheck')
                            if (!this.getModalConfirmationState(this.nameModal)) {
                                this.$refs.printConfirmationModal.show(() => {
                                    subrequestsToPrint.push(subrequest)
                                    resolve()
                                }, () => {
                                    resolve()
                                })
                            } else {
                                subrequestsToPrint.push(subrequest)
                                resolve()
                            }
                        })
                    } else {
                        subrequestsToPrint.push(subrequest)
                    }
                }

                let subrequestsWithCopies = {}
                subrequestsToPrint.forEach((item) => {
                    subrequestsWithCopies[item.id] = item.copies ? item.copies : defaultNumberOfCopies
                })

                if (subrequestsToPrint.length !== 0) {
                    let report = 'null'
                    switch (module) {
                    case 'clinical':
                        report = await clinicalSubrequestPrintService.generateLabelsWithCopiesNumber(laboratoryId, requestId, subrequestsWithCopies)
                        break
                    case 'registry':
                        report = await registrySubrequestPrintService.generateLabelsWithCopiesNumber(laboratoryId, requestId, subrequestsWithCopies)
                        break
                    }

                    await this.sendLabelsToLabelsDevice(report, device)
                }
            }
        },

        async printCaseReportFromClinical({ labId, casId }, includeAssociated) {
            // TODO: move this logic to call method
            await this.printReport(
                reportType.CASE_REPORT,
                () => clinicalCasePrintService.getCaseAPReportPdfForPrinting(labId, casId, !!includeAssociated))
        },
        async printCaseReportFromRegistry(laboratoryId, caseId, includeAssociated) {
            // TODO: move this logic to call method
            await this.printReport(
                reportType.CASE_REPORT,
                () => registryCasePrintService.getCaseAPReportPdfForPrinting(laboratoryId, caseId, !!includeAssociated))
        },

        ///

        async _sendToDevice(printFn) {
            const response =
                await PromiseUtils.toCallUnique(
                    printFn(),
                    error => ModalUtils.showErrorModalFromErrorResponse(error, this.$tc('printService.printError')))
            if (response !== null) {
                ToastUtils.showToastSuccess(this.$tc('printService.reportPrinted'))
                return true
            }
            return false
        },
        async sendReportToReportsDevice(report, device) {
            return this._sendToDevice(() => anapathPrinterRestClient.printReports(report, device))
        },
        async sendLabelsToLabelsDevice(report, device) {
            return this._sendToDevice(() => anapathPrinterRestClient.printLabels(report, device))
        },
        async sendLabelsToCassettesDevice(report, device, magazine) {
            return this._sendToDevice(() => anapathPrinterRestClient.printCassette(report, device, magazine))
        },
        async sendLabelsToCassettesDeviceForPrintMate(report, device, fileName) {
            return this._sendToDevice(() => anapathPrinterRestClient.printCassetteForPrintMate(report, device, fileName))
        },

        async checkIfRepeatPrintingInSingleEntity(entity, selectedReportType) {
            StoreUtils.updateLoading(false)
            await this.loadModalConfiguration()
            let print = false
            if (entity[selectedReportType.printed]) {
                // TODO: Prefix deletion refactoring
                const label = reportType.SUBPORTION_LABEL === selectedReportType ? entity[selectedReportType.labelFieldName] || entity.supLabel || entity.subportionLabel : entity[selectedReportType.labelFieldName]
                await new Promise((resolve) => {
                    this.modalConfirmationMessage = `${label}: ${this.$tc(selectedReportType.confirmationMessage)}`
                    this.modalConfirmationCheckMessage = this.$tc(selectedReportType.confirmationCheckMessage)
                    this.nameModal = selectedReportType.nameModal
                    if (!this.getModalConfirmationState(this.nameModal)) {
                        this.$refs.printConfirmationModal.show(() => {
                            print = true
                            resolve()
                        }, () => {
                            resolve()
                        })
                    } else {
                        print = true
                        resolve()
                    }
                })
            } else {
                print = true
            }
            return print
        },

        async checkIfRepeatedPrintingInEntitiesList(entitiesList, selectedReportType) {
            let filteredEntitiesList = []
            for (let entity of entitiesList) {
                if (await this.checkIfRepeatPrintingInSingleEntity(entity, selectedReportType)) {
                    filteredEntitiesList.push(entity)
                }
            }
            return filteredEntitiesList
        },

        getDefaultNumberOfCopiesByReportCode(reportCode) {
            for (const device of store.state.printDevices) {
                if (device.reportModel === reportCode) {
                    return device.copies
                }
            }
        },

        async getConfiguredCassetteDeviceAndMagazine(laboratoryId, priority, sampleTypeId, topographicCodeId, openMagazineModalWithoutFilter) {
            StoreUtils.updateLoading(true)
            const printServiceStatus = await this.getPrintServiceStatus()
            StoreUtils.updateLoading(false)
            if (printServiceStatus) {
                const cassetteDevices = await this.getConfiguredDevice(reportType.CASSETTE_PARAFFIN_BLOCK_PORTION_LABEL)
                if (!cassetteDevices) {
                    return [null]
                }

                return new Promise(resolve => {
                    this.$refs.modalMagazineSelector.ask(openMagazineModalWithoutFilter, laboratoryId, priority?.id, sampleTypeId, topographicCodeId,
                        magazineInfo => {
                            const magazineDevice = cassetteDevices.find(device => device.printer.name === magazineInfo.printer)

                            if (magazineDevice?.selectedPrinter) {
                                resolve([magazineDevice, magazineInfo.magazine])
                            } else {
                                resolve([null])
                            }
                        })
                })
            }
            return [null]
        },
        async printCassetteLabels(laboratoryId, caseId, sampleId, portions, priority, sampleTypeId, topographicCodeId, openMagazineModal = false) {
            const [device, magazine] = await this.getConfiguredCassetteDeviceAndMagazine(laboratoryId, priority, sampleTypeId, topographicCodeId, openMagazineModal)
            if (device) {
                const portionsToPrint = await this.checkIfRepeatedPrintingInEntitiesList(portions, reportType.CASSETTE_PARAFFIN_BLOCK_PORTION_LABEL)
                if (!portionsToPrint.length) {
                    return
                }
                if (PRINTMATE_CASSETTE_LABEL_REPORT_MODEL === device.reportModel) {
                    const [error, report] = await this.toCall(
                        portionPrintService.prepareCassettePrintLabelForPrintMate(
                            laboratoryId,
                            caseId,
                            sampleId,
                            portionsToPrint.map((portion) => {
                                return portion.id
                            }),
                            magazine
                        )
                    )
                    if (error) {
                        this.showErrorModalGlobal(this.$t('grossing.portionGrid.error.preparingCassettePrinting'))
                        return
                    }
                    const fileName = portionsToPrint[0].label + '_' + uuid()
                    await this.sendLabelsToCassettesDeviceForPrintMate(report, device, fileName)
                } else {
                    const [error, report] = await this.toCall(
                        portionPrintService.prepareCassettePrintLabel(
                            laboratoryId,
                            caseId,
                            sampleId,
                            portionsToPrint.map((portion) => {
                                return portion.id
                            })
                        )
                    )
                    if (error) {
                        this.showErrorModalGlobal(this.$t('grossing.portionGrid.error.preparingCassettePrinting'))
                        return
                    }
                    await this.sendLabelsToCassettesDevice(report, device, magazine)
                }
            }
        },
        async getPrintServiceStatus() {
            try {
                return await anapathPrinterRestClient.getPrintServiceStatus()
            } catch (e) {
                StoreUtils.updateLoading(false)
                return this.showPrintModal()
            }
        },
        async getConfiguredDevice(selectedReportType) {
            if (selectedReportType.report === reportType.CASSETTE_PARAFFIN_BLOCK_PORTION_LABEL.report) {
                return store.state.printDevices.filter(device => CASSETTE_LABEL_REPORT_MODELS.includes(device.reportModel))
            }

            for (const device of store.state.printDevices) {
                if (device.reportModel === selectedReportType.report) {
                    if (device.selectedPrinter) {
                        return device
                    }
                    return false
                }
            }
        },

        async showPrintModal() {
            return new Promise((resolve) => {
                this.$refs.modalPrint.show([() => {
                    resolve(false)
                }])
            })
        },

        showPrintConfModal() {
            this.$refs.modalPrintConfiguration.show()
        },

        async showUserConfModal() {
            await this.$refs.userConfModal.show()
        },

        showUserConfTrackingModal() {
            this.$refs.userConfTrackingModal.show()
        }
    }
}
</script>

<style scoped>
</style>
